/**
 * Print Connector - Utilitário para impressão via Print Connector
 * Envia jobs para o backend que relay para o Print Connector via WebSocket
 *
 * Fluxo:
 * 1. Frontend → Backend API (/api/print/send)
 * 2. Backend → Print Connector (via WebSocket)
 * 3. Print Connector → Impressora física
 *
 * Rotas:
 * - COZINHA: Cupom de preparo (cozinha)
 * - CAIXA: Cupom de entrega/fechamento (caixa)
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Verifica se o Print Connector está conectado ao backend
 */
export async function isConnectorOnline() {
  try {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_BASE}/api/print/connector/status`, {
      method: "GET",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: AbortSignal.timeout(3000)
    });
    if (res.ok) {
      const data = await res.json();
      return data.connected === true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Envia job de impressão para o backend (que relay para Print Connector via WebSocket)
 * @param {string} html - Conteúdo HTML (fallback para Electron/browser)
 * @param {string} route - Rota: "COZINHA" ou "CAIXA"
 * @param {Object} meta - Metadados opcionais (pedido_id, etc.)
 * @param {Object} pedido - Dados estruturados do pedido (Print Connector usa para ESC/POS direto)
 * @param {Object} empresa - Dados da empresa (nome, slogan, endereco, cnpj)
 * @returns {Promise<{success: boolean, job_id?: string, error?: string}>}
 */
export async function printViaConnector(html, route = "COZINHA", meta = {}, pedido = null, empresa = null, module = null) {
  try {
    const token = localStorage.getItem("token");
    const body = {
      content: html,
      route: route.toUpperCase(),
      meta: meta
    };
    if (pedido) body.pedido = pedido;
    if (empresa) body.empresa = empresa;
    if (module) body.module = module;

    const res = await fetch(`${API_BASE}/api/print/send`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(15000)
    });

    if (res.ok) {
      const data = await res.json();
      // Origin lock: backend retorna 200 com success:false e status='origin_offline'
      // quando o modulo esta marcado pra imprimir so no PC de origem mas esse PC esta offline.
      if (data.success === false && data.status === 'origin_offline') {
        return { success: false, status: 'origin_offline', reason: data.reason, origin_device_id: data.origin_device_id };
      }
      // skipped: o servidor descartou de propósito (chave desligada nas Preferências) — não é erro nem pede plano B
      return { success: true, job_id: data.job_id, status: data.status, skipped: data.skipped === true };
    } else {
      const err = await res.json().catch(() => ({}));
      return { success: false, error: err.detail || "Erro ao enviar para impressão" };
    }
  } catch (error) {
    return { success: false, error: error.message || "Erro de conexão com o servidor" };
  }
}

/**
 * Função principal de impressão - tenta Print Connector (via backend), depois Electron, depois navegador
 * @param {string} html - Conteúdo HTML a ser impresso
 * @param {string} route - Rota: "COZINHA" ou "CAIXA"
 * @param {string[]} printerNames - Nomes das impressoras (para Electron)
 * @param {Object} pedido - Dados do pedido para ESC/POS (opcional)
 * @param {Object} empresa - Dados da empresa (opcional)
 */
// Cache do device_id local (evita chamar IPC do Electron em cada print)
let _localDeviceIdCache = null;
let _localDeviceIdFetched = false;
async function _getLocalDeviceId() {
  if (_localDeviceIdFetched) return _localDeviceIdCache;
  if (typeof window === 'undefined' || !window.nucleoDesktop?.getPrintStatus) {
    _localDeviceIdFetched = true;
    return null;
  }
  try {
    const status = await window.nucleoDesktop.getPrintStatus();
    _localDeviceIdCache = status?.deviceId || null;
  } catch (_) {
    _localDeviceIdCache = null;
  }
  _localDeviceIdFetched = true;
  return _localDeviceIdCache;
}

export async function smartPrint(html, route = "COZINHA", printerNames = [], pedido = null, empresa = null, module = null, extraMeta = null) {
  // Incluir pedido_id no meta para rastreamento e dedup pelo watchdog
  const meta = { ...(extraMeta || {}) };
  if (pedido && (pedido.id || pedido.pedido_id)) {
    meta.pedido_id = pedido.id || pedido.pedido_id;
    if (pedido.codigo) meta.pedido_codigo = pedido.codigo;
  }

  // Identifica o PC de origem (so quando rodando dentro do Electron desktop)
  // Backend usa isso pra implementar origin-lock por modulo (ex: Balcao imprime so no PC que criou)
  const localDeviceId = await _getLocalDeviceId();
  if (localDeviceId) {
    meta.created_by_device_id = localDeviceId;
  }

  // 1. Sempre enviar para a fila de impressão via backend (Print Connector)
  // O job fica na fila e será impresso quando o connector estiver conectado
  const result = await printViaConnector(html, route, meta, pedido, empresa, module);
  if (result.success) {
    console.log(`[PrintConnector] Job enviado para fila: ${result.job_id} (status: ${result.status})`);
    return { success: true, method: "connector", job_id: result.job_id };
  }
  // Origin lock ativo + PC de origem offline → backend retorna status especial. Propagamos sem fallback.
  if (result.status === 'origin_offline') {
    console.warn(`[PrintConnector] Origin lock ativo: ${result.reason}`);
    // Toast pro lojista — usa sonner se disponivel (importado dinamicamente pra nao quebrar testes)
    try {
      const sonner = await import('sonner');
      sonner.toast?.error('Cupom não impresso', {
        description: result.reason || 'PC que criou a venda está offline',
        duration: 8000,
      });
    } catch (_) {}
    return { success: false, method: "connector", status: 'origin_offline', reason: result.reason, origin_device_id: result.origin_device_id };
  }
  console.warn("[PrintConnector] Falha ao enviar para fila:", result.error);

  // 2. Fallback: Tentar Electron nova API (se estiver no app desktop)
  if (typeof window !== "undefined" && window.Nucleo?.print) {
    try {
      const res = await window.Nucleo.print({
        content: html,
        route: route,
        type: "html",
        cut: true
      });
      if (res && res.ok !== false) {
        return { success: true, method: "electron-nucleo" };
      }
      console.warn("[Electron] Nucleo.print falhou:", res?.error);
    } catch (err) {
      console.warn("[Electron] Nucleo.print erro:", err);
    }
  }

  // 3. Fallback: Tentar Electron API antiga
  if (typeof window !== "undefined" && window.nucleoDesktop?.printHtml) {
    try {
      const res = await window.nucleoDesktop.printHtml({ html, printerNames, route });
      if (res && res.ok !== false) {
        return { success: true, method: "electron-desktop" };
      }
      console.warn("[Electron] nucleoDesktop.printHtml falhou:", res?.error);
    } catch (err) {
      console.warn("[Electron] nucleoDesktop.printHtml erro:", err);
    }
  }

  // Sem fallback para navegador — impressão sempre via fila
  return { success: false, method: "none", error: "Nenhum método de impressão disponível" };
}

